import {Injectable} from '@angular/core';
import {EditorControlService} from '../../API/Public/editor-control.service';
import {EditorControls} from '../../Utils/Objects/events';
import {
    Line3,
    Mesh,
    Object3D,
    Plane,
    Raycaster,
    Vector2,
    Vector3
} from 'three';
import {SceneService} from '../Scene/scene.service';
import {CameraService} from '../Scene/camera.service';
import {RenderService} from '../Scene/render.service';
import {ContainerService} from '../../Utils/container.service';
import {OrbitControlsService} from './orbit-controls.service';
import {CanvasEvent} from './controls';

@Injectable({
    providedIn: 'root'
})
export class MouseControlsService {

    public dblClickEnabled = true;
    private _plane: Plane;

    // get
    private _multitouch = 0;

    // brush controls
    // interaction
    private _previousEvent = null;
    private _mouse = {x: 0, y: 0};

    constructor(private eventService: EditorControlService,
                private sceneService: SceneService,
                private cameraService: CameraService,
                private renderService: RenderService,
                private containerService: ContainerService,
                // private setPointService: SetPointService,
                private orbitControlsService: OrbitControlsService
    ) {
        this.eventService.registerControl('pan', {
            events: new Map([
                [CanvasEvent.Down,   this.panStart.bind(this)],
                [CanvasEvent.Move,   this.panMove.bind(this)],
                [CanvasEvent.Up,     this.panEnd.bind(this)],
                [CanvasEvent.Double, this.setTarget.bind(this)]
            ])
        });
        this.eventService.ctrl.next('pan');
    }

    get multitouch(): number {
        return this._multitouch;
    }

    // -------------------------------------------------------------------------------------------------------------------------------------


    /**
     * handles mouse events when mouse button is clicked
     * @param event: MouseEvent
     */
    public mouseDown(event) {
        // switch (this.eventService.event) {
        //     case EditorControls.drawOnTextureRect:
        //         // this.setCameraPosition(event);
        //         // TODO: move
        //         // this.startPaint();
        //         break;
        //     // TODO: usage-check: senseburn ???
        //     case EditorControls.measuringPoints:
        //         // TODO: remove ???
        //         // this.setPointService.setPoint(event);
        //         // this.clearLassoPoints();
        //         // this.renderService.render();
        //         break;
        //     // TODO: usage-check: senseburn ???
        //     case EditorControls.editPointPostion:
        //         // TODO: remove???
        //         // this.eventService.ctrl.next('pan');
        //         // this.setPointService.changePointPosition(event);
        //         // this.clearLassoPoints();
        //         // this.renderService.render();
        //         break;
        //     default:
        //         break;
        // }

        const customControl = this.eventService.customControls.get(this.eventService.customEvent);
        if (customControl.pan && this.eventService.customEvent !== 'pan') {
            const panEvent = this.eventService.customControls.get('pan');
            panEvent.events.get(CanvasEvent.Down)(event);
        }
        customControl.events.get(CanvasEvent.Down)?.(event);
    }

    /**
     * handles mouse events when mouse is clicked and moved
     * @param event: MouseEvent
     */
    public mouseMove(event) {
        // switch (this.eventService.event) {
        //     // TODO: usage-check: senseburn ???
        //     // case EditorControls.measuringPoints:
        //     // case EditorControls.editPointPostion:
        //     //     this.changeDrawMode(true);
        //     //     if (event.buttons !== 2) {
        //     //         this.addPoint(this.get3dPointOnPlane(event));
        //     //         this.drawToScene(false);
        //     //     }
        //     //     break;
        // }

        const customEvent = this.eventService.customControls.get(this.eventService.customEvent);
        if (customEvent.pan && this.eventService.customEvent !== 'pan') {
            const panEvent = this.eventService.customControls.get('pan');
            panEvent.events.get(CanvasEvent.Move)(event);
        }
        customEvent.events.get(CanvasEvent.Move)?.(event);

    }

    /**
     * handles mouse events when mouse button is released (input finished)
     * @param event: MouseEvent
     */
    public mouseUp(event) {
        const customEvent = this.eventService.customControls.get(this.eventService.customEvent);
        if (customEvent.pan && this.eventService.customEvent !== 'pan') {
            const panEvent = this.eventService.customControls.get('pan');
            panEvent.events.get(CanvasEvent.Up)(event);
        }
        customEvent.events.get(CanvasEvent.Up)?.(event);
    }


    /**
     * fired when double clicked on model
     * --> if pan, then set rotation point
     * --> if measure then set measure point
     * @param event
     */
    public dblClick(event: MouseEvent) {
         if (this.eventService.event === EditorControls.custom) {
            const customEvent = this.eventService.customControls.get(this.eventService.customEvent);
            customEvent?.events.get(CanvasEvent.Double)?.(event);
        }
    }

    /**
     * if touch input fires with two or more pointers (two fingers)
     * @param event
     */
    public dblTouch(event: PointerEvent) {
        if (this.eventService.event !== EditorControls.drawOnTextureBrush) {
            this.startDragging(event);
        }
    }

    // TODO
    /**
     * clears selection in poly paint
     */
    public resetDraw() {
        // this.polyService.resetDraw();
    }

    /**
     * converts Events (pointerEvent --> mouseEvent)
     * @param event
     * @param canv
     * @param rect
     */
    public getSrcEvent(event, canv, rect: DOMRect) {
        this._multitouch = event.pointers ? event.pointers.length : 1;
        if (event.srcEvent) {
            // pan / tap / pinch
            if (event.srcEvent.pointerId !== undefined) {
                // pointer event
                return event.srcEvent;
            } else {
                // touch or mouse event
                if (event.srcEvent instanceof MouseEvent) {
                    return event.srcEvent;
                } else {
                    return this.convertToMouseEvent(event.srcEvent, canv, rect);
                }
            }
        } else {
            // mouse event
            return event;
        }
    }

    /**
     * converts touch events to Mouse events
     * @param event
     * @param canv
     * @param rect
     */
    public convertToMouseEvent(event: TouchEvent, canv, rect: DOMRect) {
        const touches = event.targetTouches !== undefined ? event.targetTouches.length > 0 : 0;
        const touch = touches ? event.targetTouches[0] : event.changedTouches[0];

        let type;
        switch (event.type) {
            case 'touchstart':
                type = 'pointerdown';
                break;
            case 'touchmove':
                type = 'pointermove';
                break;
            case 'touchend':
                type = 'pointerup';
                break;
            default:
                return null;
        }

        const offsetX = touch.pageX - rect.left;
        const offsetY = touch.pageY - rect.top;

        const mouseBtn = event.touches.length > 1 ? 2 /*right*/ : 1 /*left*/;
        this._multitouch = mouseBtn;

        const mouseEvent = document.createEvent('MouseEvent');

        // TODO: change to non-deprecated constructor version: new MouseEvent(...)
        return new MouseEvent(type, {
            cancelable: true,
            view: window,
            detail: 1,
            screenX: touch.screenX,
            screenY: touch.screenY,
            clientX: offsetX,
            clientY: offsetY,
            ctrlKey: false,
            altKey: false,
            shiftKey: false,
            metaKey: false,
            button: mouseBtn,
            relatedTarget: touch.target
        });
    }

    /**
     * sets camera position, control target to position
     * @param cameraPos
     * @param target
     */
    public setQuickView(cameraPos: Vector3, target: Vector3) {
        this.orbitControlsService.setQuickview(cameraPos, target);
    }


    /**
     * starts dragging model
     * @param event
     */
    private startDragging(event: PointerEvent) {
        this._previousEvent = event;
        this.computeSelectionPlane();
    }


    // TODO: move to brush service
    /**
     * returns plane intersection
     * @param point
     */
    computePlaneIntersection(point: Vector3): any {
        point.unproject(this.cameraService.camera);
        const pt = point.clone();
        const dir = point.sub(this.cameraService.camera.position).normalize();

        return this.intersectLine(pt, dir);
    }

    /**
     * returns intersection of line
     * @param point
     * @param direction
     */
    private intersectLine(point: Vector3, direction: Vector3) {
        const lineStart = point.clone().sub(direction.clone().normalize().multiplyScalar(1000));
        const lineEnd = point.clone().add(direction.clone().normalize().multiplyScalar(1000));
        const linePointCam = new Line3(lineStart, lineEnd);

        return this._plane.intersectLine(linePointCam, new Vector3(100, 100, 100));
    }

    /**
     * returns mouse position in page coordinate systems
     * @param event
     */
    public getPageMouse(event: MouseEvent): any {
        const mouse = new Vector2();
        mouse.x = (event.pageX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.pageY / window.innerHeight) * 2 + 1;
        return mouse;
    }

    /**
     * returns mouse position in canvas coordinate systems
     * @param event
     */
    public getCanvasMouse(event: MouseEvent): any {
        const mouse = new Vector2();
        mouse.x = (event.offsetX / this.cameraService.canvasWidth) * 2 - 1;
        mouse.y = -(event.offsetY / this.cameraService.canvasHeight) * 2 + 1;
        return mouse;
    }

    /**
     * returns intersection point of mouse position and 3d model
     * @param mouse
     * @param modelMesh
     */
    public getMouseModelIntersection(mouse: Vector2, modelMesh: Object3D) {
        const raycaster = new Raycaster();
        this.cameraService.camera.updateProjectionMatrix();
        raycaster.setFromCamera(mouse, this.cameraService.camera);
        return raycaster.intersectObject(modelMesh);
    }

    /**
     * returns intersections point of mouse position and 3d model
     * @param mouse
     * @param modelMesh
     */
    public getMouseModelIntersections(mouse: Vector2, modelMesh: Object3D[]) {
        const raycaster = new Raycaster();
        this.cameraService.camera.updateProjectionMatrix();
        raycaster.setFromCamera(mouse, this.cameraService.camera);
        return raycaster.intersectObjects(modelMesh);
    }


    /**
     * calculates selection plane
     * this plane is used to draw lasso selection or brush paint
     */
    computeSelectionPlane() {
        // compute the selection plane
        const camDirVec = new Vector3(0, 0, 0);
        this.cameraService.camera.getWorldDirection(camDirVec).normalize();
        const ptCamOnPlane = this.cameraService.camera.position.clone().add(camDirVec.clone().normalize().multiplyScalar(3));

        // helper plane through origin
        const plane = new Plane(camDirVec, 0);

        // distance to desired plane pt
        const dist = plane.distanceToPoint(ptCamOnPlane);
        this._plane = new Plane(camDirVec, -dist);
    }

    private panStart(event: PointerEvent) {
        if (event.buttons === 2) {
            this._previousEvent = event;
            this.computeSelectionPlane();
        }
        this._mouse.x = event.clientX;
        this._mouse.y = event.clientY;
    }

    private panMove(event: PointerEvent) {
        if (this._previousEvent != null && event.pointerId === this._previousEvent.pointerId) {
            this.adjustCameraOffset(event);
        }
    }

    private panEnd() {
        this._previousEvent = null;
    }

    /**
     * sets rotation/zoom point (control target)
     * @param event
     */
    private setTarget(event: PointerEvent) {
        const modelMesh = <Mesh>this.containerService.renderGroup.children[0];
        // get selected point
        const mouse = this.getCanvasMouse(event);
        const intersects = this.getMouseModelIntersection(mouse, modelMesh);

        // set rotation center
        if (intersects.length > 0) {
            const v = intersects[0].point;
            this.cameraService.camera.position.set(v.x, v.y, this.cameraService.camera.position.z);
            this.orbitControlsService.orbitControls.target = intersects[0].point;

            this.sceneService.getGroupByName('lightGroup').children[1].position.copy(intersects[0].point);
        } else {
            this.cameraService.camera.position.set(0, 0, this.cameraService.camera.position.z);
            this.orbitControlsService.orbitControls.target = new Vector3(0, 0, 0);
        }

        this.orbitControlsService.orbitControls.update();
        this.cameraService.resetViewOffset();
        this.renderService.render();

    }

    /**
     * adjusts camera offset --> drag model
     * @param event
     */
    private adjustCameraOffset(event: PointerEvent) {
        const mouse = this.getPageMouse(event);
        const previousMouse = this.getPageMouse(this._previousEvent);

        // offset camera view based on moved distance
        const mouseMovedX = -(mouse.x - previousMouse.x) * 15;
        const mouseMovedY = (mouse.y - previousMouse.y) * 15;
        const x = this.cameraService.viewOffsetX + mouseMovedX;
        const y = this.cameraService.viewOffsetY + mouseMovedY;

        this.cameraService.setViewOffset(x, y);
        this._previousEvent = event;

        // const raycaster = new Raycaster();
        this.cameraService.camera.updateMatrix();
        this.cameraService.camera.updateProjectionMatrix();
        // raycaster.setFromCamera(new Vector2(), this.cameraService.camera);

        this.renderService.render();
    }

    //
    // /**
    //  * custom zoom fkt, for zooming to cursor position
    //  * @param event
    //  */
    // public zoomTargetforMouse(event) {
    //     // this._cameraService.camera.updateMatrixWorld();
    //     // this._cameraService.camera.updateProjectionMatrix();
    //     // const mouse = this.getCanvasMouse(event);
    //     // const factor = 5;
    //     // const vector = new Vector3(mouse.x, mouse.y, 0.1);
    //     // vector.unproject(this._cameraService.camera);
    //     // vector.sub(this._cameraService.camera.position);
    //     // if (event.deltaY < 0) {
    //     //     this._cameraService.camera.position.addVectors(this._cameraService.camera.position, vector.setLength(factor));
    //     //     this._controlService.controls.target.addVectors(this._controlService.controls.target, vector.setLength(factor));
    //     // } else {
    //     //     this._cameraService.camera.position.subVectors(this._cameraService.camera.position, vector.setLength(factor));
    //     //     this._controlService.controls.target.subVectors(this._controlService.controls.target, vector.setLength(factor));
    //     // }
    // }

    // public zoomTargetforTouch(event: PointerEvent) {
    //     if (this._eventService.event === EditorControls.pan && this.dblClickEnabled) {
    //         // get selected point
    //         const mouse = this.getCanvasMouse(event);
    //
    //         const vec = new Vector3(); // create once and reuse
    //         const pos = new Vector3(); // create once and reuse
    //
    //         vec.set(
    //             mouse.x,
    //             mouse.y,
    //             0.5);
    //
    //         vec.unproject(this._cameraService.camera);
    //
    //         vec.sub(this._cameraService.camera.position).normalize();
    //
    //         const distance = -this._cameraService.camera.position.z / vec.z;
    //
    //         pos.copy(this._cameraService.camera.position).add(vec.multiplyScalar(distance));
    //         this._cameraService.camera.position.set(pos.x, pos.y, this._cameraService.camera.position.z);
    //         this._controlService.controls.target = pos;
    //     }
    // }



    // TODO mwimmer check if sensor is normal to camera
    // TODO: usage-check
    // private setCameraPosition(event: PointerEvent) {
    //     const modelMesh = <Mesh>this.containerService.renderGroup.children[0];
    //     // get selected point
    //     const mouse = this.getCanvasMouse(event);
    //     const intersects = this.getMouseModelIntersection(mouse, modelMesh);
    //
    //     // set rotation center
    //     if (intersects.length > 0) {
    //         const v = intersects[0].point;
    //         // TODO Rotation must be applied
    //         this.cameraService.camera.position.set(v.x, v.y, this.cameraService.camera.position.z);
    //         this.sceneService.getGroupByName('lightGroup').children[1].position.copy(intersects[0].point);
    //         this.cameraService.camera.lookAt(v);
    //     } else {
    //         this.cameraService.camera.position.set(0, 0, this.cameraService.camera.position.z);
    //     }
    //     this.cameraService.resetViewOffset();
    //     this.renderService.render();
    // }




}
